import { useMemo } from 'react';
import { MultiTurnThreadHistory } from './ThreadHistory';
import {
  MultiTurnInteractionConfiguration,
  IndexedInteractionStepResponse,
  InteractionStepResponse,
  InteractionStepParams,
  ChatApiEndpointWithId,
  CandidateMessageWithMetadata,
  InteractionStepType,
  ModelResponseSelectorStepParams,
  ContextType,
  WorkerCommentEntry,
  TextCollectionPerResponseStepParams,
  TextCollectionPerResponseStepResponse,
  StepParamsForType,
  StepParamsForTypeWithStage,
} from '@scale/shared/types/chat';
import { CandidateMessage } from '@scale/shared/types/chat-completion';
import _ from 'lodash';
import { assertDefined } from '@scale/shared/types/utils';
import { ReviewLevel } from '@scale/shared-types';
import {
  validateChatResponse,
  maybePropagateResponseChanges,
  extractPrefilledMessages,
} from '@scale/shared/chat/response';
import { getCommentIsOverallFeedback } from '@scale/shared/chat/response/feedback';
import { transformResponse } from '@scale/shared/chat/response/customerResponse';
import { IChatTaskFeatureConfig } from '@scale/shared/types/config';
import {
  flattenFields,
  flattenAnnotations,
  validateRequiredResponses,
} from '@scale/shared/textcollection/helpers';
import {
  NumberFieldAnnotation,
  TextCollectionResponse,
} from '@scale/shared/types/response/textcollection';
import { TextCollectionField } from '@scale/shared/types/params/textcollection';
import { InvalidStepIndex } from 'react-scale/components/scalers/tasks/Chat/hooks/types';
import { ChatTaskParamsStages } from '@scale/shared/chat/steps';
import { StepParamsAuxiliary } from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.computed';
import diff_match_patch from 'diff-match-patch';
import { twMerge } from 'tailwind-merge';
import { LikertNoPreferenceId } from './definitions/ModelResponseSelector/SimpleModelResponseSelectorTaskComponent';
import {
  isStepVisible,
  isStepSequentiallySkippedAndVisible,
  isConditionallyVisible,
} from '@scale/shared/chat/api';
import { allowDownstreamTemplating } from './ResponseTemplateCreator/utils';
import { getStepDescriptorByType } from './definitions';
import { ChatEventBus } from 'react-scale/components/scalers/tasks/ThreadedInteraction/events';

export const DEBOUNCE_MILLISECONDS = 3000;

export interface MultiTurnInteractionValidationResult {
  canSubmit: boolean;
  submitTooltip: string;
  invalidStepIndices: InvalidStepIndex[];
  canSubmitSBQ: boolean;
  submitTooltipSBQ: string;
}

// eslint-disable-next-line complexity
export function isMultiTurnInteractionComplete(
  config: MultiTurnInteractionConfiguration,
  responses: IndexedInteractionStepResponse[],
  workerComments: WorkerCommentEntry[],
  workerId?: string,
  reviewLevel?: ReviewLevel,
  inlineFeedbackConfig?: IChatTaskFeatureConfig['inlineFeedbackConfig'],
  completedSteps?: Record<number, boolean>,
): MultiTurnInteractionValidationResult {
  const indexedResponses = responses.map((response, index) => ({ ...response, index }));
  const history = new MultiTurnThreadHistory(config, indexedResponses, reviewLevel);
  const { before, turns, after } = history.getStepsWithResponses();
  const flattenedSteps = history.flattenStepsWithResponses(before, turns, after);

  const startOfTurnsIndex = before.length;
  let currentTurnIndex = 0;
  let currentStage = ChatTaskParamsStages.Before;
  let isFinalTurnReached = false;

  const invalidStepIndicesSBQ: InvalidStepIndex[] = [];
  const invalidStepIndices: InvalidStepIndex[] = [];

  for (let stepIndex = 0; stepIndex < flattenedSteps.length; stepIndex++) {
    const isTurnStep = stepIndex >= startOfTurnsIndex && !isFinalTurnReached;
    const turnIndex = turns.length > 0 && isTurnStep ? currentTurnIndex : undefined;
    const stage = currentStage;

    const [stepParams, response] = flattenedSteps[stepIndex];
    if (!stepParams || !response) {
      const errorMessage = 'Not all steps have been completed.';
      invalidStepIndices.push({ stepIndex, errorMessage });
      continue;
    }

    if (stepIndex === startOfTurnsIndex - 1) {
      currentStage = ChatTaskParamsStages.Turn;
    }
    // Increment turn count when a MultiTurnContinue step is reached
    if (stepParams.type === InteractionStepType.MultiTurnContinue) {
      currentTurnIndex++;

      // This is the final turn if shouldContinue is false
      if (!response.context.shouldContinue) {
        isFinalTurnReached = true;
        currentStage = ChatTaskParamsStages.After;
      }
    }

    if (
      isFinalTurnReached &&
      stepParams.type === InteractionStepType.QualityMeasurement &&
      response.contextType === ContextType.Skipped &&
      isStepVisible(stepParams, reviewLevel)
    ) {
      const errorMessage = `Complete the "${stepParams.title}" step to send the task back.`;
      invalidStepIndicesSBQ.push({ stepIndex, errorMessage });
    }

    // Step has been submitted before but is currently being edited
    if (completedSteps && !_.isNil(completedSteps[stepIndex]) && !completedSteps[stepIndex]) {
      const errorMessage = 'Some steps are currently being edited and must be saved.';
      invalidStepIndices.push({
        stepIndex,
        errorMessage,
        turnIndex,
      });
      continue;
    }

    const step = getStepDescriptorByType(stepParams.type);

    if (!step) {
      throw new Error(`Step with type "${stepParams.type}" doesn't exists!`);
    }

    // Individual step response validation
    const { canSubmit, submitTooltip } = step.isResponseValid(
      response,
      {
        ...stepParams,
        stage,
        turn: !_.isNil(turnIndex) && stage === ChatTaskParamsStages.Turn ? turnIndex : -1,
        responseIndex: response.index,
      },
      history.until(stepIndex),
      reviewLevel,
    );
    if (!canSubmit) {
      invalidStepIndices.push({ stepIndex, errorMessage: submitTooltip, turnIndex });
      continue;
    }

    // Inline feedback validation
    const { canSubmit: canSubmitInlineFeedback, submitTooltip: submitTooltipInlineFeedback } =
      validateInlineFeedbackForStep(
        stepParams,
        stepIndex,
        workerComments,
        workerId,
        reviewLevel,
        inlineFeedbackConfig,
      );
    if (!canSubmitInlineFeedback) {
      invalidStepIndices.push({ stepIndex, errorMessage: submitTooltipInlineFeedback, turnIndex });
      continue;
    }
  }

  const lastTurn = turns[turns.length - 1];
  const turnsFinished = lastTurn.complete && !lastTurn.shouldContinue;
  const afterResponses = after.map(a => a[1]).filter(r => !!r);
  const afterFinished = afterResponses.length === config.after.length;
  const { valid, message } = validateChatResponse(config, responses);

  const {
    canSubmit: canSubmitAtLeastOneInlineComment,
    submitTooltip: submitTooltipAtLeastOneInlineComment,
  } = validateAtLeastOneInlineComment(workerComments, inlineFeedbackConfig, reviewLevel, workerId);

  if (!valid) {
    const errorMessage = message ?? 'The responses are not valid.';
    invalidStepIndices.push({
      stepIndex: -1,
      errorMessage,
    });
  } else if (!turnsFinished) {
    const errorMessage = 'Not all turns have been completed.';
    invalidStepIndices.push({ stepIndex: -1, errorMessage });
  } else if (!config.endSessionCondition && turns.length < Number(config.minTurns)) {
    /*
     * If no end session condition is set, check the minimum turn requirement
     * JSONata end session conditions are responsible for checking any minimum turn requirements
     */
    const errorMessage = `The minimum number of turns (${config.minTurns}) has not been reached.`;
    invalidStepIndices.push({
      stepIndex: -1,
      errorMessage,
    });
  } else if (!afterFinished) {
    const errorMessage = 'Not all conversation-level questions have been completed.';
    invalidStepIndices.push({
      stepIndex: -1,
      errorMessage,
    });
  } else if (areAnyOverallFeedbackRatingsMissing(workerComments, inlineFeedbackConfig)) {
    const errorMessage = 'Some comments have not been rated.';
    invalidStepIndices.push({
      stepIndex: -1,
      errorMessage,
    });
  } else if (!canSubmitAtLeastOneInlineComment) {
    invalidStepIndices.push({ stepIndex: -1, errorMessage: submitTooltipAtLeastOneInlineComment });
  }

  return {
    canSubmit: !invalidStepIndices.length,
    submitTooltip: invalidStepIndices.length ? invalidStepIndices[0].errorMessage : '',
    invalidStepIndices,
    canSubmitSBQ: !invalidStepIndicesSBQ.length,
    submitTooltipSBQ: invalidStepIndicesSBQ.length ? invalidStepIndicesSBQ[0].errorMessage : '',
  };
}

function areAnyOverallFeedbackRatingsMissing(
  workerComments: WorkerCommentEntry[],
  inlineFeedbackConfig?: IChatTaskFeatureConfig['inlineFeedbackConfig'],
) {
  const requireRatingComments = inlineFeedbackConfig?.requireRatingComments ?? false;

  return (
    requireRatingComments &&
    workerComments
      .filter(comment => getCommentIsOverallFeedback(comment))
      .some(comment => _.isNil(comment.feedbackResponse))
  );
}

export function canAddWorkerCommentsForStep(
  reviewLevel: ReviewLevel,
  workerComments: WorkerCommentEntry[],
  workerId?: string,
) {
  const isReviewer = reviewLevel !== ReviewLevel.Attempt;
  return isReviewer && workerComments.every(comment => comment?.author !== workerId);
}

function validateAtLeastOneInlineComment(
  workerComments: WorkerCommentEntry[],
  inlineFeedbackConfig?: IChatTaskFeatureConfig['inlineFeedbackConfig'],
  reviewLevel?: ReviewLevel,
  workerId?: string,
) {
  // If the project configs are undefined, this will default to allowing submission
  const requireAddingAtLeastOneComment =
    inlineFeedbackConfig?.requireAddingAtLeastOneComment ?? false;
  const newWorkerComments = workerComments.filter(
    comment => !_.isNil(workerId) && comment.author === workerId,
  );
  const canEditComments = reviewLevel !== ReviewLevel.Attempt;

  if (canEditComments && requireAddingAtLeastOneComment && newWorkerComments.length === 0) {
    return { canSubmit: false, submitTooltip: 'Please add at least one comment on this task' };
  }
  return { canSubmit: true, submitTooltip: '' };
}

function validateInlineComments(
  stepIndex: number,
  workerComments: WorkerCommentEntry[],
  requireRatingComments: boolean,
  workerId?: string,
) {
  const stepWorkerComments = workerComments.filter(comment => {
    if (!comment.childId) {
      return false;
    }
    const responseIndex = parseInt(comment.childId!.split('-')[1]);
    return responseIndex === stepIndex;
  });

  const existingWorkerComments = stepWorkerComments.filter(comment => {
    return _.isNil(workerId) || comment.author !== workerId;
  });

  const missingRating = existingWorkerComments.some(
    comment => comment.feedbackResponse === undefined,
  );
  if (requireRatingComments && missingRating) {
    return {
      canSubmit: false,
      submitTooltip: 'Some comments have not been rated.',
    };
  } else {
    return {
      canSubmit: true,
      submitTooltip: '',
    };
  }
}

function validateInlineFeedbackForStep(
  stepParams: StepParamsForType<InteractionStepType>,
  stepIndex: number,
  workerComments: WorkerCommentEntry[],
  workerId?: string,
  reviewLevel?: ReviewLevel,
  inlineFeedbackConfig?: IChatTaskFeatureConfig['inlineFeedbackConfig'],
) {
  if (!isStepVisible(stepParams, reviewLevel)) {
    return { canSubmit: true, submitTooltip: '' };
  }

  // If the project configs are undefined, this will default to allowing submission
  const requireRatingComments = inlineFeedbackConfig?.requireRatingComments ?? false;

  return validateInlineComments(stepIndex, workerComments, requireRatingComments, workerId);
}

export function useMultiTurnInteractionValidation({
  config,
  responses,
  isLoading,
  hasResponseLoadingError,
  isSubmitting,
  allStepsCompleted,
  workerComments,
  workerId,
  reviewLevel,
  inlineFeedbackConfig,
  hasActiveStepResponseLinterError,
  completedSteps,
  prefetchingInProgress,
}: {
  config: MultiTurnInteractionConfiguration;
  responses: IndexedInteractionStepResponse[];
  isLoading: boolean;
  hasResponseLoadingError: boolean;
  isSubmitting: boolean;
  allStepsCompleted: boolean;
  workerComments: WorkerCommentEntry[];
  workerId?: string;
  reviewLevel?: ReviewLevel;
  inlineFeedbackConfig?: IChatTaskFeatureConfig['inlineFeedbackConfig'];
  hasActiveStepResponseLinterError?: boolean;
  completedSteps?: Record<number, boolean>;
  prefetchingInProgress?: boolean;
}): MultiTurnInteractionValidationResult {
  const { submitTooltip, canSubmit, invalidStepIndices, canSubmitSBQ, submitTooltipSBQ } = useMemo(
    () =>
      isMultiTurnInteractionComplete(
        config,
        responses,
        workerComments,
        workerId,
        reviewLevel,
        inlineFeedbackConfig,
        completedSteps,
      ),
    [
      config,
      responses,
      reviewLevel,
      workerComments,
      inlineFeedbackConfig,
      completedSteps,
      workerId,
    ],
  );

  if (hasActiveStepResponseLinterError) {
    return {
      canSubmit: false,
      submitTooltip: 'Please fix any active linter errors before submitting.',
      invalidStepIndices,
      canSubmitSBQ,
      submitTooltipSBQ,
    };
  } else if (hasResponseLoadingError) {
    return {
      canSubmit: false,
      submitTooltip: 'There was an error loading the task. Please refresh or contact support.',
      invalidStepIndices,
      canSubmitSBQ: false,
      submitTooltipSBQ: 'There was an error loading the task. Please refresh or contact support.',
    };
  } else if (isLoading) {
    return {
      canSubmit: false,
      submitTooltip: 'Loading...',
      invalidStepIndices,
      canSubmitSBQ: false,
      submitTooltipSBQ: 'Loading...',
    };
  } else if (isSubmitting) {
    return {
      canSubmit: false,
      submitTooltip: 'Submitting...',
      invalidStepIndices,
      canSubmitSBQ: false,
      submitTooltipSBQ: 'Submitting...',
    };
  } else if (!allStepsCompleted) {
    return {
      canSubmit: false,
      submitTooltip: 'Please complete all the steps before submitting.',
      invalidStepIndices,
      canSubmitSBQ,
      submitTooltipSBQ,
    };
  } else if (prefetchingInProgress) {
    return {
      canSubmit: false,
      submitTooltip: 'Finalizing task...',
      invalidStepIndices,
      canSubmitSBQ,
      submitTooltipSBQ,
    };
  }

  return { submitTooltip, canSubmit, invalidStepIndices, canSubmitSBQ, submitTooltipSBQ };
}

export function transformStepResponsesForMultiTurn(
  config: MultiTurnInteractionConfiguration,
  responses: InteractionStepResponse[],
  hideUnmetConditionsEnabled: boolean,
) {
  const indexedResponses: IndexedInteractionStepResponse[] = responses.map((r, index) => ({
    ...r,
    index,
  }));
  let transformedResponses = undefined;
  try {
    transformedResponses = transformResponse(
      { responses: indexedResponses },
      config,
      hideUnmetConditionsEnabled,
    );
  } catch (e) {
    // Skip
  }
  return {
    transformedResponses,
  };
}

export function extractVariablesFromTextCollectionResponse(
  response: TextCollectionResponse,
): Record<string, string> {
  const variables: Record<string, string> = {};

  for (const [fieldId, value] of Object.entries(response.annotations)) {
    if (value.type === 'number' || value.type === 'text') {
      variables[fieldId] = value.response[0].toString();
    }
  }

  return variables;
}

export function extractVariablesFromPreviousResponses(
  responses: IndexedInteractionStepResponse[],
): Record<string, string> {
  const variables: Record<string, string> = {};
  for (const response of responses) {
    if (response.type === InteractionStepType.TextCollection) {
      const { annotations } = response.context.response;
      for (const [fieldId, value] of Object.entries(annotations)) {
        if (value.response) {
          variables[fieldId] = value.response[0].toString();
        }
      }
    }
    if (response.type === InteractionStepType.PromptInput) {
      variables.first_prompt_input = response.output;
    }
  }
  return variables;
}

export function generateShuffledRange(rangeSize: number) {
  return _.shuffle(_.range(rangeSize));
}

export function buildCandidatesWithId(
  candidates: CandidateMessage[],
  chatModels: ChatApiEndpointWithId[],
) {
  return candidates.map((c, i) => {
    assertDefined(c.sourceId);
    const candidateId = c.sourceId;
    return {
      id: candidateId,
      endpoint: chatModels[i],
      ...c,
    };
  });
}

export function shuffleCandidates(candidates: CandidateMessageWithMetadata[], order: number[]) {
  return _.compact(order.map(i => candidates[i]));
}

export function removeDuplicatesFromCandidates(candidates: CandidateMessageWithMetadata[]) {
  const noDuplicates: CandidateMessageWithMetadata[] = [];
  candidates.reduce((lookup, candidate) => {
    if (!lookup.has(candidate.message.content)) {
      noDuplicates.push(candidate);
      lookup.add(candidate.message.content);
    }
    return lookup;
  }, new Set<string>());
  return noDuplicates;
}

export function setResponseForIndexWithPropagation(
  config: MultiTurnInteractionConfiguration,
  newResponse: InteractionStepResponse,
  index: number,
  responses: IndexedInteractionStepResponse[],
) {
  if (index < responses.length - 1) {
    const newResponses = _.cloneDeep(responses);
    newResponses.splice(index, 1, {
      ...newResponse,
      index,
    });
    maybePropagateResponseChanges(config, newResponses, index, {
      previousStepResponse: responses.at(index),
      templateVariables: config.templateVariables,
    });
    return newResponses;
  } else {
    const newResponses = [
      ...responses.slice(0, index),
      {
        ...newResponse,
        index,
      },
    ];
    return newResponses;
  }
}

export function getForcedStaticStepParams(
  step: InteractionStepParams,
  inlineTemplating: boolean = false,
) {
  if (step.type === InteractionStepType.ModelResponseSelector) {
    const transformedStepParams = _.cloneDeep(step) as ModelResponseSelectorStepParams;
    transformedStepParams.params.provided_model_responses =
      transformedStepParams.params.provided_model_responses ?? [];
    transformedStepParams.params.provided_model_responses = [
      ...transformedStepParams.params.chat_models.map((model: ChatApiEndpointWithId) => ({
        id: model.id,
        sourceId: model.id,
        content: '',
      })),
      ...transformedStepParams.params.provided_model_responses,
    ];
    transformedStepParams.params.chat_models = [];
    if (inlineTemplating) {
      // If inline templating is enabled, we need to be able to see the model response step
      transformedStepParams.params.skip_selection = false;
    }
    return transformedStepParams;
  }
  return step;
}

export function useTextCollectionStepValidation({
  readOnly,
  resultChanged,
  submitCodeError,
  response,
  fields,
  templateVariables,
  isValidEmptyResponse,
  allAttachmentsViewed,
  autoCritiqueIsLoading,
  codeGenerationEnforcementPassed,
}: {
  readOnly?: boolean;
  resultChanged: boolean;
  submitCodeError?: string;
  response: TextCollectionResponse;
  fields: TextCollectionField[];
  templateVariables?: Record<string, string>;
  isValidEmptyResponse?: boolean;
  allAttachmentsViewed?: boolean;
  autoCritiqueIsLoading?: boolean;
  codeGenerationEnforcementPassed?: boolean;
}) {
  const { submitTooltip, canSubmit } = useMemo(() => {
    const { isValid: responseIsValid, tooltip: responseValidationTooltip } =
      validateRequiredResponses(
        Object.values(flattenFields(fields)),
        flattenAnnotations(response?.annotations),
        templateVariables,
      );

    const disabledTooltip = responseValidationTooltip ?? 'Please complete all required fields';

    return {
      canSubmit: responseIsValid,
      submitTooltip: responseIsValid ? '' : disabledTooltip,
    };
  }, [response, fields, templateVariables]);

  if (isValidEmptyResponse) {
    return {
      canSubmit: true,
      submitTooltip: '',
    };
  } else if (readOnly) {
    return {
      canSubmit: false,
      submitTooltip: 'This section is read-only',
    };
  } else if (!resultChanged) {
    return {
      canSubmit: false,
      submitTooltip: 'No changes detected',
    };
  } else if (submitCodeError) {
    return {
      canSubmit: false,
      submitTooltip: submitCodeError,
    };
  } else if (autoCritiqueIsLoading) {
    return {
      canSubmit: false,
      submitTooltip: 'Auto-critique is in progress',
    };
  } else if (!allAttachmentsViewed) {
    return {
      canSubmit: false,
      submitTooltip: 'Please view all attachments before submitting',
    };
  } else if (codeGenerationEnforcementPassed === false) {
    return {
      canSubmit: false,
      submitTooltip: 'Please run the code successfully before submitting',
    };
  }
  return { submitTooltip, canSubmit };
}

/**
 * Retrieves the numerical scores for a given text collection field
 */
export function getScoresForTextCollectionField(
  annotations: Record<string, TextCollectionResponse>,
  fieldId: string,
) {
  // Values are either an int or NaN
  return _.mapValues(annotations, response => {
    const flatAnnotations = flattenAnnotations(response.annotations);
    const annotation = flatAnnotations[fieldId];
    const value = (annotation as NumberFieldAnnotation | undefined)?.response[0];
    if (!value) return NaN;
    return parseInt(value);
  });
}

/**
 * Retrieves the field name to validate scores against
 */
export function getFieldNameToCheck(
  textCollectionFields: TextCollectionField[],
  fieldIdToCheck: string,
) {
  const fieldToCheck = textCollectionFields.filter(
    (f: TextCollectionField) => f.field_id === fieldIdToCheck,
  );
  if (!fieldToCheck || fieldToCheck.length !== 1) {
    return undefined;
  }
  const fieldNameToCheck = fieldToCheck[0].title;

  return fieldNameToCheck;
}

/**
 * Validates that the text collection responses are consistent with the selected response
 */
export function areTextCollectionResponsesConsistent(
  annotations: Record<string, TextCollectionResponse>,
  fields: TextCollectionField[],
  fieldToCheck: string,
  selectedId: string,
) {
  if (selectedId === '' || selectedId === LikertNoPreferenceId) {
    return { isValid: true, errorMessage: '' };
  }

  const scoresForField = getScoresForTextCollectionField(annotations, fieldToCheck);
  const maxScore = _.max(Object.values(scoresForField));
  const selectedScore = scoresForField[selectedId];

  const fieldNameToCheck = getFieldNameToCheck(fields, fieldToCheck);
  const errorMessage = fieldNameToCheck
    ? `Please ensure that your "${fieldNameToCheck}" ratings are consistent with your selected response from above.`
    : 'Please ensure that your ratings are consistent with your selected response from above.';
  const isValid = selectedScore === maxScore || _.isUndefined(maxScore);

  return { isValid, errorMessage };
}

export function isModelSelectionConsistent(
  textCollectionPerResponseStepResponse: TextCollectionPerResponseStepResponse | null,
  textCollectionPerResponseStepParams: TextCollectionPerResponseStepParams | null,
  selectedId: string,
) {
  if (
    _.isNil(textCollectionPerResponseStepResponse) ||
    _.isNil(textCollectionPerResponseStepParams) ||
    textCollectionPerResponseStepResponse.contextType === ContextType.Skipped
  ) {
    return { isValid: true, errorMessage: '' };
  }

  const { fieldToCheck: fieldIdToCheck, fields } = textCollectionPerResponseStepParams.params;
  const annotations = textCollectionPerResponseStepResponse.context.annotations;

  if (_.isNil(fieldIdToCheck)) {
    return { isValid: true, errorMessage: '' };
  }

  const fieldName = getFieldNameToCheck(fields, fieldIdToCheck);
  const errorMessage = fieldName
    ? `Please ensure that your selected model response is consistent with your "${fieldName}" ratings from above.`
    : 'Please ensure that your selected model response is consistent with your ratings from above.';
  const { isValid } = areTextCollectionResponsesConsistent(
    annotations,
    fields,
    fieldIdToCheck,
    selectedId,
  );

  return { isValid, errorMessage };
}

/**
 * Validates that the model ranking is consistent with the text collection responses
 */
export function isModelRankingConsistent(
  textCollectionPerResponseStepResponse: TextCollectionPerResponseStepResponse | null,
  textCollectionPerResponseStepParams: TextCollectionPerResponseStepParams | null,
  rankedCandidatesIds: string[][],
) {
  if (
    _.isNil(textCollectionPerResponseStepResponse) ||
    _.isNil(textCollectionPerResponseStepParams) ||
    textCollectionPerResponseStepResponse.contextType === ContextType.Skipped ||
    !rankedCandidatesIds.length
  ) {
    return { isValid: true, errorMessage: '' };
  }
  const { fieldToCheck: fieldIdToCheck, fields } = textCollectionPerResponseStepParams.params;
  const annotations = textCollectionPerResponseStepResponse.context.annotations;

  if (_.isNil(fieldIdToCheck)) {
    return { isValid: true, errorMessage: '' };
  }

  const scoresbyField = getScoresForTextCollectionField(annotations, fieldIdToCheck);
  const flatRankedCandidateIds = _.flatten(rankedCandidatesIds);
  const allRankingScores = flatRankedCandidateIds.map(id => scoresbyField[id]);

  const fieldName = getFieldNameToCheck(fields, fieldIdToCheck);
  const errorMessage = fieldName
    ? `Please ensure that your rankings are consistent with your "${fieldName}" ratings from above.`
    : 'Please ensure that your rankings are consistent with your ratings from above.';

  const isValid = allRankingScores.every(
    (score, index, rankingScores) =>
      index === rankingScores.length - 1 || score >= rankingScores[index + 1],
  );

  return { isValid, errorMessage };
}

const preserveGradedMRSSelection = (response: IndexedInteractionStepResponse) => {
  const selectedIndex = _.get(response, 'context.selectedIndex');
  const isObjective = _.get(response, 'context.isObjective');
  const likertValueRaw = _.get(response, 'context.likertValueRaw');
  const likertMagnitude = _.get(response, 'context.likertMagnitude');
  const likertValueUnrandomized = _.get(response, 'context.likertValueUnrandomized');
  const justificationRaw = _.get(response, 'context.justificationRaw');
  const justificationUnrandomized = _.get(response, 'context.justificationUnrandomized');

  return {
    ...(selectedIndex && { selectedIndex }),
    ...(isObjective && { isObjective }),
    ...(likertValueRaw && { likertValueRaw }),
    ...(likertMagnitude && { likertMagnitude }),
    ...(likertValueUnrandomized && { likertValueUnrandomized }),
    ...(justificationRaw && { justificationRaw }),
    ...(justificationUnrandomized && { justificationUnrandomized }),
  };
};

/**
 * Syncs the content from the history responses into an inferredResponses object
 * The inferredResponses responses have "contextType: skipped" matching the provided inferredResponsesBase
 */
export function syncBenchmarkResponses(
  history: MultiTurnThreadHistory,
  params: MultiTurnInteractionConfiguration,
  inferredResponsesBase: IndexedInteractionStepResponse[],
): {
  valid: boolean;
  inferredResponses?: IndexedInteractionStepResponse[];
} {
  const inferredResponses: IndexedInteractionStepResponse[] = [];
  const stepsWithResponses: [any | undefined, IndexedInteractionStepResponse | undefined][] =
    history.getFlattenedStepsWithResponses();

  // Only attempt to sync the responses if the responses are compatible
  if (
    inferredResponsesBase.length !== stepsWithResponses.length ||
    _.some(stepsWithResponses, ([_step, response], index) => {
      return response?.type !== inferredResponsesBase[index].type;
    })
  ) {
    return {
      valid: false,
    };
  }

  stepsWithResponses.map(([step, response], index) => {
    if (step && allowDownstreamTemplating(step.params.type)) {
      let copiedResponse = _.cloneDeep(response!);
      if (inferredResponsesBase[index].contextType === ContextType.Skipped) {
        copiedResponse.contextType = ContextType.Skipped;
      }
      if (!response) {
        return {
          valid: false,
        };
      }

      if (response.type === InteractionStepType.ModelResponseSelector) {
        // Need to copy over non-graded properties only
        const preservedContext = preserveGradedMRSSelection(inferredResponsesBase[index]);
        copiedResponse = {
          ...copiedResponse,
          context: {
            ...copiedResponse.context,
            ...preservedContext,
          },
        };
      }
      inferredResponses.push(copiedResponse);
    } else {
      inferredResponses.push(inferredResponsesBase[index]);
    }
  });

  inferredResponses.map((_, index) => {
    maybePropagateResponseChanges(params, inferredResponses, index, {
      benchmarkEdit: true,
      templateVariables: params.templateVariables,
    });
  });

  const { valid } = validateChatResponse(params, inferredResponses, {
    includePerResponseValidation: true,
  });

  return { valid, inferredResponses };
}

/**
 * Returns the readOnly override for a given step so that tasks that use turn locking/incremental turn progression
 * only allow the last turn to be editable.
 */
export const getReadOnlyOverrideForStep = (
  config: MultiTurnInteractionConfiguration,
  stepParams: StepParamsForType<InteractionStepType> & StepParamsAuxiliary,
) => {
  let readOnlyOverride = false;
  if (config.turnLocking && config.maxTurns) {
    if (stepParams.stage === ChatTaskParamsStages.Before) {
      // before stages should only be editable if we're editing the first turn
      readOnlyOverride = Number(config.maxTurns) !== 1;
    } else if (
      stepParams.stage === ChatTaskParamsStages.Turn &&
      stepParams.type === InteractionStepType.MultiTurnContinue
    ) {
      // Any new turn we add attributes the new MultiTurnContinue step to the previous turn,
      // so we need the last two MultiTurnContinue steps to be editable
      readOnlyOverride = stepParams.turn + 1 < Number(config.maxTurns) - 1;
    } else if (stepParams.stage === ChatTaskParamsStages.Turn) {
      // If not a MultiTurnContinue step, only the last turn should be editable
      readOnlyOverride = stepParams.turn !== Number(config.maxTurns) - 1;
    }
    // otherwise, after turns should always be editable
  }
  return readOnlyOverride;
};

/**
 * Gets the diff content between two texts and returns the diff content as styled spans
 * @param originalText
 * @param newText
 * @returns
 */
export const getTwoTextDiffContent = (
  originalText: string | undefined,
  newText: string | undefined,
) => {
  if (_.isNil(originalText) || _.isNil(newText)) {
    return null;
  }

  // eslint-disable-next-line new-cap
  const dmp = new diff_match_patch();
  const result = dmp.diff_main(originalText, newText);

  dmp.diff_cleanupSemantic(result);

  const [diffResultOriginalText, diffResultNewText] = [
    result.filter(part => part[0] <= 0),
    result.filter(part => part[0] >= 0),
  ].map(diffText =>
    diffText.map((part, i) => (
      <span
        key={`diff_two_${i}`}
        className={twMerge(
          part[0] === 1
            ? 'bg-success-50 text-success-600'
            : part[0] === -1
            ? 'bg-danger-50 text-danger-600'
            : '',
        )}
      >
        {part[1]}
      </span>
    )),
  );

  return {
    diffResultOriginalText,
    diffResultNewText,
  };
};

export const visibleStepFilter = ({
  step,
  responses,
  stepParams,
  reviewLevel,
  excludeHiddenModelResponseSelectorSteps,
  templateVariables,
}: {
  step: StepParamsForTypeWithStage<InteractionStepType>;
  responses: IndexedInteractionStepResponse[];
  stepParams: StepParamsForTypeWithStage<InteractionStepType>[];
  reviewLevel: ReviewLevel;
  excludeHiddenModelResponseSelectorSteps?: boolean;
  templateVariables?: Record<string, string>;
}) => {
  const isHiddenModelResponseSelectorStep =
    excludeHiddenModelResponseSelectorSteps &&
    step.type === InteractionStepType.ModelResponseSelector &&
    !!step.params.skip_selection;

  return (
    step.title &&
    !isHiddenModelResponseSelectorStep &&
    isStepVisible(step, reviewLevel) &&
    isConditionallyVisible(step, responses, step.responseIndex, templateVariables, reviewLevel) &&
    !isStepSequentiallySkippedAndVisible(
      stepParams,
      responses,
      step.responseIndex,
      reviewLevel,
      templateVariables,
    )
  );
};

export function isPromptEditableAtIndex(
  config: MultiTurnInteractionConfiguration,
  promptIndex: number,
) {
  const { taskingUserMessages } = extractPrefilledMessages(config.initialMessages);
  const userMessage = taskingUserMessages?.[promptIndex];

  // If the content is empty, then the prompt should be editable
  if (_.isNil(userMessage) || !userMessage.content) {
    return true;
  }
  return !!userMessage.userEditable;
}

export const computeShouldSkipLinting = ({
  projectId,
  assignmentId,
  stepParams,
  stepResponse,
  eventBus,
  disableLinter,
}: {
  projectId?: string;
  assignmentId?: string;
  stepParams: StepParamsForType<InteractionStepType>;
  stepResponse: InteractionStepResponse;
  eventBus: ChatEventBus;
  disableLinter?: boolean;
}): boolean => {
  if (disableLinter) {
    eventBus.emit('linter:skipped', {
      reason: 'LinterDisabled',
    });
    return true;
  }
  if (!projectId) {
    eventBus.emit('linter:skipped', {
      reason: 'MissingProjectId',
    });
    return true;
  }
  if (!assignmentId) {
    eventBus.emit('linter:skipped', {
      reason: 'MissingAssignmentId',
    });
    return true;
  }

  if (stepParams.type === InteractionStepType.ContextDirections) {
    eventBus.emit('linter:skipped', {
      reason: 'NotLintableStep',
    });
    return true;
  }
  if (
    stepParams.type === InteractionStepType.MultiTurnContinue &&
    stepResponse.context.shouldContinue === false
  ) {
    eventBus.emit('linter:skipped', {
      reason: 'LastTurn',
    });
    return true;
  }

  if (!stepParams.id) {
    throw new Error('Step ID cannot be undefined');
  }
  return false;
};

export function extractTextFromRegex(text: string, regex: string): string {
  const match = text.match(regex);
  return match && match[0] ? match[0] : text;
}
